import React, { useCallback } from 'react';

import { ModelPreview, ModelPreviewProps } from '@spazious/dollhouse-engine';
import { PiInfoDuotone } from '@spazious/dollhouse-icons';
import { useStore } from '@spazious/dollhouse-store';
import { UnitType } from '@spazious/dollhouse-types';
import { Spinner, ToggleButton, ToggleButtonProps } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { useDeviceInfo, useUnitConverter } from '../../hooks/index.js';
import { ModelDimensionInfo } from '../ModelDimensionInfo/index.js';

export interface ModelPreviewCardProps extends ModelPreviewProps {}

export interface InfoToggleButtonProps extends ToggleButtonProps {
  toggle: () => void;
}

const InfoToggleButton: React.FC<InfoToggleButtonProps> = ({ toggle, ...props }) => {
  const onPointerDown = useEventCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    toggle();
  });
  return (
    <ToggleButton
      size="extraSmall"
      style={{
        position: 'absolute',
        top: 'var(--spacing-padding)',
        right: 'var(--spacing-padding)',
        backgroundColor: 'transparent',
      }}
      onPointerDown={onPointerDown}
      onTouchStart={(event: React.TouchEvent<HTMLButtonElement>) => event.stopPropagation()}
      {...props}
    >
      <PiInfoDuotone />
    </ToggleButton>
  );
};

export const ModelPreviewCard: React.FC<ModelPreviewCardProps> = ({ ...restProps }) => {
  const measurementsUnit = useStore((state) =>
    state.measurementsUnit === UnitType.Meter ? UnitType.Centimeter : UnitType.Feet
  );
  const { displayUnit } = useUnitConverter();
  const { isTouchScreen } = useDeviceInfo();

  const labelFormatter = useCallback(
    (value: string | number) => {
      const showUnit = true;
      const fractionDigits = measurementsUnit === UnitType.Feet ? 2 : 0;
      return displayUnit(value, showUnit, measurementsUnit, fractionDigits);
    },
    [displayUnit, measurementsUnit]
  );

  return (
    <ModelPreview
      dimensionInfoComponent={ModelDimensionInfo}
      fallback={<Spinner />}
      infoToggleButtonComponent={InfoToggleButton}
      labelFormatter={labelFormatter}
      showInfoTrigger={isTouchScreen ? 'button' : 'hover'}
      {...restProps}
    />
  );
};
